/**
 * The rails — view → section → screen — from the frozen canvas (`proto/cider-shell.js` items
 * and each product page's rail), with every item a plain noun. Sections are the registry's
 * `section` strings, so the nav can never disagree with the policy; `nav.test.ts` checks that
 * and the noun rule. `was` keeps the canvas's original words where a title was renamed, so the
 * rename is visible and reversible.
 */
import type { ViewId } from '@cider/ui';

export interface Screen {
  /** The canvas screen id, for the address. */
  id: string;
  title: string;
  /** The canvas's words, when renamed for the noun rule. */
  was?: string;
  /** Other words search finds it by — a drawn name renamed by a ruling. */
  also?: string;
  /** The hover reason the drawing gives the entry (Rebuild 34–36's Money rail). */
  why?: string;
}

export interface Section {
  /** Exactly the registry's `section` for this view. */
  title: string;
  screens: readonly Screen[];
}

const S = (id: string, title: string, was?: string): Screen =>
  was ? { id, title, was } : { id, title };
/** An entry with the drawing's hover reason; `was` only where the noun differs from the drawing's words. */
const W = (id: string, title: string, was: string, why: string): Screen =>
  was === title ? { id, title, why } : { id, title, was, why };

export const RAILS: Record<ViewId, readonly Section[]> = {
  volunteers: [
    // Export 21's Volunteers rail, in its own order (Volunteers Part 1, C). The section headings
    // are the registry's, which the canvas draws differently in two places — SIGNUPS & ROSTER
    // and SETTINGS (YEAR); every year setting lives in Festival Admin, so that one has no rail
    // here. `was` keeps the canvas's words wherever the noun rule renamed one.
    {
      title: 'Shifts',
      screens: [
        S('vop01', 'Shift builder', 'Season shift builder'),
        S('vop18', 'Staffing queue & forecast'),
        S('vop15', 'Requirements & briefs'),
        S('vop25', 'Shift proposals'),
      ],
    },
    {
      title: 'Signups',
      screens: [
        S('vop05', 'Roster & people'),
        S('vop10', 'Signup ops'),
        S('missed', 'Missed check-outs'),
      ],
    },
    {
      title: 'Reminders & verification',
      screens: [
        S('vop09', 'Staffing', 'Where we stand'),
        S('form', 'Signup form', 'The signup form'),
        S('portal', 'My Shifts'),
        S('vop24', 'Reminders'),
        S('sms', 'Day-of text', 'The day-of text'),
        S('spot', 'Spot opened', 'A spot opened'),
        S('vop23', 'Contact verification'),
      ],
    },
    {
      title: 'Families & commitments',
      screens: [
        // James, Oct 8: families made by hand. Canvas defect #187.
        S('families', 'Families'),
        S('cm09', 'Family standing', 'Where each family stands'),
        S('cm12', 'Credit run', 'The credit run'),
        S('cm13', 'Sponsor → family'),
        S('cm08', 'Exemptions'),
        S('cm14', 'Statements'),
        // Canvas defect #99: "Declared for the year" and its four worklists have no screen of
        // their own on the canvas (Doors in Part 1).
        S('declared', 'Declared'),
        // ADR 0029, placed by ADR 0033 (James, Oct 14): ServiceHours.dc.html — the volunteer
        // chair's three screens join Families & commitments.
        S('sh-queue', 'Letters to sign'),
        S('sh-groups', 'Group list', 'A group leader’s list'),
        S('sh-person', 'On the person'),
      ],
    },
    {
      title: 'Check-in booths',
      screens: [
        S('day04b', 'Check-in', 'Check in'),
        S('day04c', 'Walk-up'),
        S('day04d', 'Badges'),
        S('day04e', 'Check-out', 'Check out'),
        S('day04f', 'Void'),
        S('day04h', 'Offline day list', 'The offline day list'),
        S('day04a', 'Device enrollment'),
      ],
    },
    { title: 'Recruiting & prospects', screens: [S('cl06', 'Private links & regulars')] },
    // ADR 0027: the drawing's SETTINGS (YEAR) — Volunteers' named values, read, no Save.
    { title: 'Settings (year)', screens: [S('set', 'Year settings')] },
  ],
  money: [
    // The rail fold (Rebuild 34–36; James, Oct 15): sixty-two entries to thirty-seven under six
    // sections, each with the drawing's hover reason. A folded screen is a part of its new home
    // (`?part=`), and every old address redirects there (`money/moves.ts`). Titles stay nouns;
    // `was` keeps the drawing's words. Not on the rail until built: the cash strip, Items by margin.
    {
      title: 'Today',
      screens: [
        W(
          'today',
          'Standing',
          'Where we stand',
          'four figures · the three things with a person · overdue rows from The finance year',
        ),
        W(
          'director',
          'Director',
          'The director’s Money',
          'one page when a director opens Money — red lines · Decide · the four figures · the forum P&L · overdue',
        ),
      ],
    },
    {
      title: 'Ask · Approve',
      screens: [
        W(
          'bud00',
          'Home',
          'Budget · my path',
          'the phase strip and your steps · every Budget screen is reached from a step',
        ),
        W(
          'bud01',
          'History',
          'Read the baseline',
          'every line from its rule · “was:” · the receipts assumption',
        ),
        W(
          'bud05',
          'Festival',
          'Read the festival · two sides',
          'receipts · shared costs · obligations · TO DISTRIBUTE',
        ),
        W(
          'bud06',
          'Area',
          'Work an area',
          'the finance director’s page with pencils · the chair’s read beside it',
        ),
        W(
          'bud08',
          'Review',
          'Answer the Asks',
          'one Director before lock · two names after · declined with a sentence',
        ),
        W('bud11', 'Owners', 'Assign owners', 'who owns what · two roles · accounts and areas'),
        W(
          'accounts',
          'Accounts',
          'Keep the accounts',
          'the tree: add · sub-account · rename · move · map · archive · merge',
        ),
        W('bud03', 'Committed', 'Watch the lines', 'spoken for · spent · remaining · 90% / 100%'),
        W('bud04', 'Forecast', 'Forecast', 'where the year lands at this pace'),
      ],
    },
    {
      title: 'Spend · Pay',
      screens: [
        W(
          'fdwork',
          'Worklist',
          'Work the list',
          'expenses to record · POs to match · bills to pay · red lines · drafts, incoming invoices and chair receipts fold in here',
        ),
        W(
          'linecard',
          'Line card',
          'Read a line',
          'budget · spoken for · spent · over in red with the Ask prompt',
        ),
        W(
          'po',
          'Purchase orders',
          'Open & match POs',
          '“they’ll bill us” · opened after the order · standing contracts',
        ),
        W(
          'bills',
          'Bills to pay',
          'Pay bills',
          'matched bills on the check run · two signers over $750',
        ),
        W('checks', 'Print checks', 'Print checks', 'the run · stubs · signers'),
        W(
          'exp',
          'Small expenses & mileage',
          'Pay expenses',
          '“I spent” · mileage · petty cash · reimbursements',
        ),
        W('vendors', 'Vendors', 'Keep the vendors', 'deactivate with a reason · never delete'),
      ],
    },
    {
      title: 'Sell · Count',
      screens: [
        W(
          'sq02',
          'Live sales',
          'Watch the registers',
          'selling right now · register health · Square vs the drawer',
        ),
        W(
          'sq01',
          'Item mapping',
          'Keep the items',
          'ours and Square’s · cost · margin · the price-point sentence',
        ),
        W('sq06', 'Items by margin', 'Read items by margin', 'the menu meeting’s screen'),
        W('sq03', 'On hand', 'Count what’s on hand', 'par levels · usage'),
        W('orders', 'Orders', 'Read the orders', 'prepaid pickup · tickets · passes'),
        W(
          'settle',
          'Settlement by product',
          'Settle by product',
          'rides · raffle · bingo statements',
        ),
        W(
          'sponsors',
          'Receivables',
          'Collect what we’re owed',
          'sponsors and pledges · aging · yield · which calls to make',
        ),
        W(
          'funds',
          'Donations & funds',
          'Record gifts & vouchers',
          'donations · funds · meal vouchers',
        ),
      ],
    },
    {
      title: 'Cash · Bank',
      screens: [
        W(
          'cashstrip',
          'Cash strip',
          'Run the cash strip',
          'per day, bag by bag: issued · out · picked up · counted · deposited · seed bags, deposits and the bank run are its parts',
        ),
        W('countroom', 'Count room', 'Count tonight', 'the count room · two names on every count'),
        W('bank', 'Bank match', 'Match the bank', 'rules that code lines · reclassify with a log'),
      ],
    },
    {
      title: 'Close · Report',
      screens: [
        W(
          'finyear',
          'Finance year',
          'The finance year',
          'twelve months · dated obligations from Year settings and the law · due · who · done / overdue · the yearly bills live here',
        ),
        W('close', 'Month close', 'Close the month', 'what blocks it'),
        W(
          'closeout',
          'Year end',
          'End the year',
          'eleven things in order · variance read back in numbers',
        ),
        W(
          'giving',
          'Distributions',
          'Send the money out',
          'AOD · distributions · CRE · seed · two directors',
        ),
        W(
          'reports',
          'Reports',
          'Print reports',
          'the statement · the P&L · the 1099 run · the AOD package',
        ),
        W(
          'journal',
          'Journal',
          'Read every entry',
          'the journal · who did what · the parish account · documents on their records',
        ),
        W('imp01', 'Import', 'Prove the books', 'bringing 1.0 in · proving each year'),
      ],
    },
  ],
  marketing: [
    {
      title: 'Signage & menu boards',
      screens: [
        S('sig08', 'Menu boards'),
        S('sig09', 'Emergency layer'),
        S('sig07', 'Screen registry'),
        S('sig10', 'Playlists by phase'),
        S('sig11', 'Proof & publish'),
      ],
    },
    {
      title: 'Public website',
      screens: [
        S('pub01', 'Site', 'The festival site'),
        S('pub02', 'Signup chain'),
        S('pub09', 'Pages & blocks'),
        S('med01', 'Photos & files'),
        S('nav01', 'Menus & redirects'),
        S('frm01', 'Forms'),
        S('seo01', 'Titles & sharing'),
        S('web01', 'Web', 'Web analytics'),
      ],
    },
    {
      title: 'Sponsors & recognition',
      screens: [
        // Defect #89: the one-organization rule needs a home the canvas did not draw.
        S('sp00', 'Organizations'),
        S('sp01', 'Sponsors', 'This year’s sponsors'),
        S('sp02', 'Sponsor record', 'A sponsor'),
        S('sp03', 'Artwork'),
        S('sp04', 'Recognition', 'Where they appear'),
        S('sp05', 'Asks', 'The January asks'),
        S('sp06', 'Gifts in kind'),
        S('pub05', 'Sponsor portal'),
      ],
    },
    {
      title: 'Announcements & templates',
      screens: [
        S('com11', 'Templates', 'Template family'),
        S('com13', 'Announcements', 'Push & announcements'),
        S('news', 'Newsletters'),
      ],
    },
    {
      title: 'Tickets & presale',
      screens: [
        S('tk01', 'Ticket types & prices'),
        S('tk05', 'Seatings & will-call'),
        S('tk02', 'Checkout', 'Buying a ticket'),
        S('promo', 'Promo codes'),
        S('questions', 'Checkout questions', 'Questions at checkout'),
        S('rd01', 'Presale settings'),
        S('rd03', 'Presale flow', 'Public presale flow'),
        S('rd04', 'Chair store'),
        S('pub06', 'Public presale', 'Presale, public'),
      ],
    },
    { title: 'Guest survey', screens: [S('cl03', 'Guest survey', 'Debrief & guest survey')] },
    {
      title: 'Raffles & baskets',
      screens: [
        S('raf01', 'Raffles', 'The raffles'),
        S('raf02', 'Ticket books & sellers'),
        S('raf03', 'Sales & drawing', 'Sales & the drawing'),
        S('raf04', 'Basket donors'),
        S('raf05', 'Baskets', 'The baskets'),
        S('raf06', 'Gift cards'),
        S('raf08', 'Online raffle tickets', 'Raffle tickets online'),
        S('raf09', 'License', 'What the license allows'),
        S('raf07', 'State filing', 'What the state needs'),
      ],
    },
  ],
  ops: [
    {
      title: 'Watchtower',
      screens: [
        S('wt02', 'Desk', 'The operations desk'),
        S('wt01', 'Field app', 'Opening the field app'),
        S('wt09', 'Directors’ board'),
        S('wt08', 'Money wall'),
      ],
    },
    {
      title: 'Incidents & weather',
      screens: [
        S('wt03', 'Incidents'),
        S('wt04', 'Weather console'),
        S('wt10', 'Mutual aid & contacts'),
        S('lost', 'Lost & found'),
      ],
    },
    {
      title: 'Gates, doors & keys',
      screens: [
        S('wt05', 'Gates & direction'),
        S('tk03', 'Head count', 'Counting people in'),
        S('tk06', 'Hall door', 'The hall door'),
        // Drawn as the box office; renamed by James, Oct 17 ("office" is a killed word).
        S('tk04', 'Ticket window', 'The ticket window'),
        S('wt06', 'Radios & keys'),
      ],
    },
    { title: 'Readiness', screens: [S('wt07', 'Opening walk', 'The opening walk')] },
    {
      title: 'Rides day-of',
      screens: [
        S('rd02', 'Orders', 'Orders & late payments'),
        S('rd05', 'Booth redemption'),
        S('rd06', 'Stock'),
        S('rd08', 'Ride chair', 'The ride chair’s screen'),
      ],
    },
    {
      title: 'Supplies & vendors',
      screens: [
        S('lft10', 'Requests', 'Requests board'),
        S('lft11', 'Runner’s round', 'The runner’s round'),
        S('lft12', 'Stock on hand'),
        S('lft13', 'Equipment'),
        S('lft14', 'Orders & suppliers'),
        S('lft15', 'Usage', 'What we used'),
        S('kitchen', 'Kitchen board', 'The kitchen board'),
        S('storage', 'Storage', 'Where things live'),
        S('contracts', 'Contracts', 'Contracted services'),
        S('psigns', 'Signs'),
      ],
    },
    {
      title: 'Grounds & power',
      screens: [
        S('gr01', 'Site plan', 'The site, year by year'),
        S('gr02', 'Power plan'),
        S('gr03', 'Setup & strike'),
        S('gr04', 'Safety walk', 'The safety walk'),
      ],
    },
  ],
  chairs: [
    { title: 'My roster', screens: [S('ch01', 'My roster')] },
    { title: 'My open shifts', screens: [S('ch02', 'My open shifts')] },
    { title: 'Check-in / out', screens: [S('ch03', 'Check-in / out', 'Check-in / check-out')] },
    { title: 'Requests', screens: [S('ch04', 'Requests')] },
    { title: 'My money', screens: [S('ch05', 'My money', 'My area’s money')] },
    { title: 'Safety card', screens: [S('ch06', 'Safety card')] },
    {
      title: 'More',
      screens: [
        S('ch08', 'Menu board'),
        S('ch09', 'Radios'),
        S('ch07', 'Messages'),
        S('ch11', 'Square change list'),
        S('ch10', 'Rides', 'The ride chair’s screen'),
        S('ch13', 'Raffle', 'The raffle chair’s screen'),
        S('ch14', 'Baskets', 'The basket chair’s screen'),
        S('ch12', 'Closeout', 'Closeout home'),
        // Canvas defect 84: the yearly packet, reached from the chair app's home line.
        S('ch16', 'Your packet'),
      ],
    },
  ],
  admin: [
    {
      title: 'People & roles',
      screens: [
        S('roles', 'Roles', 'The eleven roles'),
        S('pipe', 'Chairs', 'Finding next year’s chairs'),
        S('succ', 'Successors', 'Areas one person deep'),
        S('person', 'People', 'A person, and how they get in'),
        // Canvas defects 47 and 48: the identity worklists and the 1.0 people import live here.
        S('ident', 'Identity', 'Nobody exists twice'),
        S('pimport', 'Import', 'The 1.0 people snapshot'),
        S('invites', 'Invitations'),
        // Canvas defect 78: areas by year with their director, chairs, targets and rules.
        S('areas', 'Areas', 'Who runs which area'),
        S('offboard', 'Offboarding', 'Stepping down'),
        // Rebuild 59 #ful01 (WORKLIST F17; ADR 0104): a family’s computed credit beside its ruling.
        S('ful01', 'Family fulfillment', 'Families · 2026 fulfillment'),
      ],
    },
    {
      title: 'Messages & deliverability',
      screens: [
        S('com10', 'Deliverability', 'Deliverability home'),
        S('com12', 'Message log', 'Message log per person'),
        S('com14', 'SMS console'),
        S('com15', 'Send preview & throttle'),
        // Canvas defect 61: the Emails page's templates, edited where the sends are read.
        S('com16', 'Templates', 'Every message, its sender, its version'),
        // Rebuild 32's Emails.dc.html (James, Oct 14): every email CIDER sends, as it arrives.
        S('emails', 'Emails'),
        // Rebuild 32's Notifications.dc.html: a phone for each kind of person, as they arrive.
        S('notifications', 'Notifications'),
        // The routing page of Notifications.dc.html (export 25), as its own screen — every
        // event the design raises, and whether CIDER raises it yet (James, Sep 27).
        S('routing', 'Routing table'),
      ],
    },
    {
      title: 'Meetings',
      screens: [
        S('mtg01', 'Meetings'),
        S('mtg02', 'Chairs behind on meetings', 'Chairs behind'),
        S('mtg03', 'Chair roster', 'Who runs each area'),
      ],
    },
    {
      title: 'Debrief & next-year plan',
      screens: [
        S('cl02', 'Area closeouts', 'Per-area closeout'),
        S('cl04', 'Lessons & plan', 'Lessons → plan'),
      ],
    },
    {
      title: 'Year settings',
      screens: [
        // Canvas defect 53: the year's dates and Money's four have no screen of their own on the canvas.
        // The year's settings in six rail rows (James, Oct 13 — canvas defect 242): the festival
        // keeps the canvas's id and words; Money · Volunteers & families · Sponsors · the site ·
        // state filings follow in that order.
        // Rebuild 41's Year settings (fayear; Oct 19 overnight): every setting ruled this season,
        // each SET or DEFAULT with who and when — first, above the six rail rows it summarizes.
        S('fayear', 'Year settings', 'Every setting ruled this season'),
        S('ysett', 'Festival', 'The dates, the numbers, the cadence'),
        S('ys-money', 'Money'),
        S('ys-volunteers', 'Volunteers & families'),
        S('ys-sponsors', 'Sponsors'),
        S('ys-site', 'Site', 'The site'),
        S('ys-filings', 'State filings'),
        S('reqs', 'Background checks & training', 'What each role must have'),
        // Canvas defect 84: the yearly onboarding packet's definition.
        S('packet', 'Onboarding packet', 'What every chair completes each year'),
        // Canvas defect 92: the year's sponsor levels with the benefits matrix (Sponsors Part 2).
        S('splev', 'Sponsor levels', 'What each level costs and earns'),
        S('commit', 'Commitments'),
        // Booth.dc.html's stations page (Rebuild 29, state 33) — drawn on the booth's poster.
        S('stations', 'Booth stations'),
        S('golive', 'Go live'),
        S('rollover', 'Next year', 'Opening next year'),
      ],
    },
    {
      title: 'System',
      screens: [
        // Canvas defect 52: a Status screen reading /status.
        S('status', 'Status', 'Store, database, worker, backups'),
        S('access', 'Access issues', 'Can’t get in'),
        // Rebuild 50 #acclog · #acclog2 (WORKLIST P4): every auth event, and every live session.
        S('acclog', 'Access log', 'Who signed in, and from where'),
        S('sessions', 'Sign-in rules'),
        // Rebuild 50 #numbers · #numbers2 · #numbers3 (WORKLIST F5): Decide · Season, filed under System.
        S('numbers', 'The festival in numbers'),
        S('integ', 'Integrations', 'What we are connected to'),
      ],
    },
    {
      // Ruling 91's platform set, which a director does not hold. Export 22 draws these four
      // under their own head; canvas defects #70–#73 are the screens they had none of.
      title: 'Platform',
      screens: [
        S('devices', 'Devices'),
        // Rebuild 48/50 #needsperson (WORKLIST P5): one row per figure, the 7 AM digest's rows.
        S('needsperson', 'What needs a person'),
        S('rtpl', 'Role templates & overrides'),
        S('sysset', 'System settings'),
        S('bglass', 'Break-glass'),
      ],
    },
  ],
};

/**
 * **The screens whose name really is a sentence**, by id, each with the reason it is exempt from
 * the noun rule. A screen earns a line here only when the sentence *is* its name everywhere else
 * — in the code, in its own tests, on the canvas — so that calling it a noun on the rail and a
 * sentence in every other breath is the thing that would be wrong.
 */
export const NOUN_EXEMPT: Record<string, string> = {
  audit:
    'Who did what — its name in the code, its tests and on the canvas; "Audit" was the rail’s ' +
    'word alone, and a director looking for it looked for this one (James, Oct 1 — A8).',
  needsperson:
    'What needs a person — its name on the canvas (Rebuild 48/50 #needsperson), in the 7 AM ' +
    'digest, in Money’s Today and in the work list (P5); the Square rescue queue lands "on What ' +
    'needs a person" in the code already.',
  numbers:
    'The festival in numbers — its name on all three of Rebuild 50’s pages (#numbers · #numbers2 · ' +
    '#numbers3) and in the one-pager the directors print; "Festival in numbers" names nothing.',
};

/** A title is a noun: no sentence openers, no verbs of the "what we / who is" kind, no end punctuation. */
/**
 * **The school's own rail** (ADR 0033, James Oct 14): Commitment.dc.html — the school's ledger,
 * state by state. It is not on the Volunteers rail: a person holding the school role sees this
 * rail and nothing else; a director, who holds the ledger's door too, opens it by address.
 */
export const SCHOOL_RAIL: readonly Section[] = [
  {
    title: 'School ledger',
    screens: [
      S('cm-ledger', 'Families by hours'),
      S('cm-family', 'One family'),
      S('cm-shortfall', 'Shortfall letters', 'The shortfall letter'),
      S('cm-buyout', 'Buy-outs', 'The buy-out'),
      S('cm-appeals', 'Appeals'),
      S('cm-tuition', 'Tuition hand-off', 'Hand-off to tuition'),
      S('cm-close', 'Year close', 'Closing the year'),
      S('cm-rules', 'Commitment rules', 'The commitment rules'),
    ],
  },
];

/** Every section a view's addresses can open: its rail, and for Volunteers the school's rail. */
export function railsOf(view: ViewId): readonly Section[] {
  return view === 'volunteers' ? [...RAILS[view], ...SCHOOL_RAIL] : RAILS[view];
}

const SENTENCE =
  /^(the|a|an|what|who|where|how|when|which|why|can|is|are|finding|counting|bringing|proving|closing|ending|sending|starting|selling|buying|match|excuse)\b|[.?!]$|\bwe\b|\bmy area's\b/i;

export function isNoun(title: string): boolean {
  return !SENTENCE.test(title);
}

/** Every rename, for the flags: view · section · was → is. */
export function renames(): { view: ViewId; section: string; was: string; is: string }[] {
  const out: { view: ViewId; section: string; was: string; is: string }[] = [];
  for (const view of Object.keys(RAILS) as ViewId[])
    for (const s of railsOf(view))
      for (const sc of s.screens)
        if (sc.was) out.push({ view, section: s.title, was: sc.was, is: sc.title });
  return out;
}
